import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
})
export class PaginationComponent {
  currentPage = input.required<number>();
  totalPages = input.required<number>();
  maxVisible = input<number>(5);

  pageChange = output<number>();

  protected hasPrev = computed(() => this.currentPage() > 1);
  protected hasNext = computed(() => this.currentPage() < this.totalPages());

  /** Returns page numbers to display; null = ellipsis */
  protected visiblePages = computed((): (number | null)[] => {
    const total = this.totalPages();
    const current = this.currentPage();
    const max = this.maxVisible();

    if (total <= max) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const half = Math.floor(max / 2);
    let start = Math.max(2, current - half);
    let end = Math.min(total - 1, current + half);

    // Adjust window if near edges
    if (current - half < 2) end = Math.min(total - 1, max - 1);
    if (current + half > total - 1) start = Math.max(2, total - max + 2);

    const pages: (number | null)[] = [1];
    if (start > 2) pages.push(null); // leading ellipsis
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total - 1) pages.push(null); // trailing ellipsis
    pages.push(total);

    return pages;
  });

  protected goTo(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.currentPage()) return;
    this.pageChange.emit(page);
  }
}
